import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronRight, ChevronDown, RefreshCw, AlertCircle, Users, Phone, Mail, X } from 'lucide-react';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';
import TiersTagsFilter from '../components/TiersTagsFilter';
import DepartementFilter from '../components/DepartementFilter';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtEur(n) {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
}

function fmtDate(s) {
  if (!s || s === 'False' || s === '') return '—';
  try {
    const [y, m, d] = s.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
  } catch { return s; }
}

const BUCKETS = [
  { key: 'non_echu',  label: 'Non échu'  },
  { key: 'j1_30',    label: '1 – 30 j'  },
  { key: 'j31_60',   label: '31 – 60 j' },
  { key: 'j61_90',   label: '61 – 90 j' },
  { key: 'j91_plus', label: '> 90 j'    },
  { key: 'total',    label: 'Total'      },
];

const BUCKET_COLORS = {
  non_echu: '#16A34A',
  j1_30:    '#CA8A04',
  j31_60:   '#EA580C',
  j61_90:   '#DC2626',
  j91_plus: '#7F1D1D',
  total:    '#1D4ED8',
};

// ─── Popover contacts ──────────────────────────────────────────────────────────

function ContactsPopover({ entrepriseId, partnerNom }) {
  const [open, setOpen]         = useState(false);
  const [contacts, setContacts] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [pos, setPos]           = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const popRef = useRef(null);

  // Fermer au clic extérieur
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Fermer à la touche Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const toggle = async (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }

    // Calculer la position relative au bouton
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) {
      setPos({ top: rect.bottom + 6, left: Math.max(8, rect.left - 620) });
    }
    setOpen(true);

    if (contacts !== null) return; // déjà chargé
    setLoading(true);
    try {
      const r = await api.getBalanceAgeeContacts(entrepriseId);
      setContacts(r.data.contacts || []);
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={toggle}
        data-testid={`btn-contacts-${entrepriseId}`}
        title="Voir les contacts destinataires"
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 22, height: 22, borderRadius: 5, border: '1px solid',
          borderColor: open ? '#93C5FD' : 'var(--border)',
          background: open ? '#EFF6FF' : 'transparent',
          color: open ? '#1D4ED8' : '#9CA3AF',
          cursor: 'pointer', flexShrink: 0, transition: 'all .15s',
        }}
      >
        <Users size={12} />
      </button>

      {open && (
        <div
          ref={popRef}
          data-testid={`popover-contacts-${entrepriseId}`}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            zIndex: 9999,
            width: 1260,
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            overflow: 'hidden',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
            padding: '10px 14px', borderBottom: '1px solid var(--border)',
            background: '#F8FAFC' }}>
            <div style={{ display:'flex', alignItems:'center', gap: 8 }}>
              <Users size={14} style={{ color: '#1D4ED8' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                Contacts — {partnerNom}
              </span>
            </div>
            <button onClick={() => setOpen(false)}
              style={{ border:'none', background:'none', cursor:'pointer', color:'#6B7280', display:'flex', padding:2 }}>
              <X size={14} />
            </button>
          </div>

          {/* Corps */}
          <div style={{ overflowY: 'visible' }}>
            {loading ? (
              <div style={{ padding: '20px 14px', textAlign:'center', fontSize:13, color:'#9CA3AF' }}>
                Chargement…
              </div>
            ) : !contacts || contacts.length === 0 ? (
              <div style={{ padding: '20px 14px', textAlign:'center', fontSize:13, color:'#9CA3AF' }}>
                Aucun contact avec le tag "Destinataire factures" ou "Destinataire relance"
              </div>
            ) : (
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12, tableLayout:'fixed' }}>
                <colgroup>
                  <col style={{ width: 120 }} />{/* Prénom */}
                  <col style={{ width: 150 }} />{/* Nom */}
                  <col style={{ width: 165 }} />{/* Fonction */}
                  <col style={{ width: 195 }} />{/* Communication */}
                  <col style={{ width: 165 }} />{/* Ligne directe */}
                  <col style={{ width: 165 }} />{/* Portable */}
                  <col style={{ width: 300 }} />{/* Email */}
                </colgroup>
                <thead>
                  <tr style={{ background:'#F1F5F9' }}>
                    {['Prénom', 'Nom', 'Fonction', 'Communication', 'Ligne directe', 'Portable', 'Email'].map(h => (
                      <th key={h} style={{ padding:'6px 10px', textAlign:'left', fontWeight:600,
                        color:'#475569', borderBottom:'1px solid #E2E8F0',
                        whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c, i) => (
                    <tr key={c.personne_id}
                      style={{ background: i % 2 === 0 ? '#fff' : '#F8FAFC',
                        borderBottom: '1px solid #F1F5F9' }}>
                      <td title={c.prenom} style={{ padding:'6px 10px', color:'#374151', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c.prenom || '—'}</td>
                      <td title={c.nom} style={{ padding:'6px 10px', fontWeight:500, color:'#111827', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c.nom || '—'}</td>
                      <td title={c.fonction} style={{ padding:'6px 10px', color:'#6B7280', fontStyle:'italic', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{c.fonction || '—'}</td>
                      <td style={{ padding:'6px 10px' }}>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
                          {(c.automation_tags || []).length > 0
                            ? (c.automation_tags || []).map((t, ti) => (
                                <span key={ti} style={{
                                  fontSize:10, padding:'1px 6px', borderRadius:9999,
                                  background: (t.couleur || '#6B7280') + '22',
                                  color: t.couleur || '#6B7280',
                                  border: `1px solid ${(t.couleur || '#6B7280')}55`,
                                  whiteSpace:'nowrap',
                                }}>{t.nom}</span>
                              ))
                            : <span style={{ color:'#D1D5DB' }}>—</span>
                          }
                        </div>
                      </td>
                      <td title={c.ligne_directe} style={{ padding:'6px 10px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {c.ligne_directe
                          ? <a href={`tel:${c.ligne_directe}`} style={{ color:'#1D4ED8', textDecoration:'none', display:'flex', alignItems:'center', gap:4, overflow:'hidden' }} onClick={e=>e.stopPropagation()}>
                              <Phone size={10} style={{ flexShrink:0 }} /><span style={{ overflow:'hidden', textOverflow:'ellipsis' }}>{c.ligne_directe}</span>
                            </a>
                          : <span style={{ color:'#D1D5DB' }}>—</span>}
                      </td>
                      <td title={c.telephone_mobile} style={{ padding:'6px 10px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {c.telephone_mobile
                          ? <a href={`tel:${c.telephone_mobile}`} style={{ color:'#1D4ED8', textDecoration:'none', display:'flex', alignItems:'center', gap:4, overflow:'hidden' }} onClick={e=>e.stopPropagation()}>
                              <Phone size={10} style={{ flexShrink:0 }} /><span style={{ overflow:'hidden', textOverflow:'ellipsis' }}>{c.telephone_mobile}</span>
                            </a>
                          : <span style={{ color:'#D1D5DB' }}>—</span>}
                      </td>
                      <td title={c.email} style={{ padding:'6px 10px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                        {c.email
                          ? <a href={`mailto:${c.email}`} style={{ color:'#1D4ED8', textDecoration:'none', display:'flex', alignItems:'center', gap:4, overflow:'hidden' }} onClick={e=>e.stopPropagation()}>
                              <Mail size={10} style={{ flexShrink:0 }} /><span style={{ overflow:'hidden', textOverflow:'ellipsis' }}>{c.email}</span>
                            </a>
                          : <span style={{ color:'#D1D5DB' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ─── Ligne tiers (dépliable) ─────────────────────────────────────────────────

function TiersRow({ tiers, expanded, onToggle }) {
  return (
    <>
      {/* Ligne tiers */}
      <tr
        onClick={onToggle}
        className="cursor-pointer select-none border-b transition-colors hover:bg-blue-50"
        style={{ borderColor: '#E5E7EB', background: expanded ? '#EFF6FF' : '#F8FAFC' }}
        data-testid={`tiers-row-${tiers.partner_id}`}
      >
        {/* Nom */}
        <td className="py-3 px-4" style={{ minWidth: 240 }}>
          <div className="flex items-center gap-2">
            {expanded
              ? <ChevronDown size={15} className="text-blue-600 flex-shrink-0" />
              : <ChevronRight size={15} className="text-gray-400 flex-shrink-0" />}
            <span className="font-semibold text-sm" style={{ color: '#111827' }}>
              {tiers.partner_nom}
            </span>
            {/* Icône contacts — uniquement si entreprise_id connu (pas "anon_") */}
            {tiers.partner_id && !String(tiers.partner_id).startsWith('anon_') && (
              <ContactsPopover
                entrepriseId={tiers.partner_id}
                partnerNom={tiers.partner_nom}
              />
            )}
          </div>
        </td>
        {/* Date échéance — vide sur la ligne tiers */}
        <td className="py-3 px-4 text-sm text-gray-400 text-center" style={{ minWidth: 110 }}>—</td>
        {/* Buckets */}
        {BUCKETS.map(({ key }) => {
          const val = tiers[key];
          const isTotal = key === 'total';
          const hasValue = Math.abs(val) >= 0.005;
          return (
            <td key={key} className="py-3 px-4 text-right text-sm font-semibold tabular-nums"
              style={{
                minWidth: 120,
                color: hasValue ? BUCKET_COLORS[key] : '#9CA3AF',
                background: isTotal ? (expanded ? '#DBEAFE' : '#EFF6FF') : 'transparent',
                borderLeft: isTotal ? '2px solid #BFDBFE' : undefined,
              }}>
              {fmtEur(val)}
            </td>
          );
        })}
      </tr>

      {/* Lignes détail factures + règlements */}
      {expanded && tiers.lignes.map((lg) => (
        <tr key={lg.id} className="border-b"
          style={{ borderColor: '#E5E7EB', background: '#FAFCFF' }}
          data-testid={`ligne-row-${lg.id}`}>
          {/* Référence */}
          <td className="py-2 pl-10 pr-4">
            <div className="flex items-center gap-2">
              {lg.is_payment && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: '#DCFCE7', color: '#16A34A', border: '1px solid #BBF7D0', flexShrink: 0 }}>
                  RGL
                </span>
              )}
              <div className="text-sm font-medium" style={{ color: lg.is_payment ? '#16A34A' : '#374151', fontStyle: lg.is_payment ? 'italic' : 'normal' }}>
                {lg.piece}
              </div>
            </div>
          </td>
          {/* Date d'échéance */}
          <td className="py-2 px-4 text-sm text-center tabular-nums" style={{ color: '#6B7280' }}>
            {fmtDate(lg.date_echeance)}
          </td>
          {/* Montants */}
          {BUCKETS.map(({ key }) => {
            const isTotal = key === 'total';
            const val = isTotal ? lg.montant : (lg.bucket === key ? lg.montant : 0);
            const hasValue = Math.abs(val) >= 0.005;
            return (
              <td key={key} className="py-2 px-4 text-right text-sm tabular-nums"
                style={{
                  color: lg.is_payment
                    ? (hasValue ? '#16A34A' : '#D1D5DB')
                    : (hasValue ? BUCKET_COLORS[key] : '#D1D5DB'),
                  fontWeight: hasValue ? 500 : 400,
                  fontStyle: lg.is_payment ? 'italic' : 'normal',
                  background: isTotal ? (lg.is_payment ? '#DCFCE7' : '#F0F7FF') : 'transparent',
                  borderLeft: isTotal ? '2px solid #BFDBFE' : undefined,
                }}>
                {hasValue ? fmtEur(val) : '0,00 €'}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

// ─── Ligne totaux ─────────────────────────────────────────────────────────────

function TotauxRow({ totaux }) {
  return (
    <tr style={{ background: '#1E3A8A', borderTop: '2px solid #1D4ED8' }}>
      <td className="py-3 px-4" colSpan={2}>
        <span className="text-sm font-bold text-white uppercase tracking-wide">Total général</span>
      </td>
      {BUCKETS.map(({ key }) => {
        const val = totaux?.[key] ?? 0;
        const hasValue = Math.abs(val) >= 0.005;
        return (
          <td key={key} className="py-3 px-4 text-right text-sm font-bold tabular-nums text-white"
            data-testid={`total-${key}`}>
            {hasValue ? fmtEur(val) : '0,00 €'}
          </td>
        );
      })}
    </tr>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function BalancesAgees() {
  const { activeAgences } = useSociete();
  const [tab, setTab]           = useState('client');
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [expanded, setExpanded] = useState({});
  const [sortCol, setSortCol]   = useState('partner_nom');
  const [sortDir, setSortDir]   = useState(1);
  // Filtre tags client (tiers_tags — Party Model, onglet Clients uniquement) — mémorisé dans le navigateur
  const [includeTags, setIncludeTags] = useState(() => {
    try { return JSON.parse(localStorage.getItem('balance_agee_tags_filter'))?.inc || []; } catch { return []; }
  });
  const [excludeTags, setExcludeTags] = useState(() => {
    try { return JSON.parse(localStorage.getItem('balance_agee_tags_filter'))?.exc || []; } catch { return []; }
  });
  const handleTagsChange = (inc, exc) => {
    setIncludeTags(inc);
    setExcludeTags(exc);
    try { localStorage.setItem('balance_agee_tags_filter', JSON.stringify({ inc, exc })); } catch { /* quota */ }
  };
  // Filtre département (préfixe code postal) — onglets Clients ET Fournisseurs — mémorisé dans le navigateur
  const [depts, setDepts] = useState(() => {
    try { return JSON.parse(localStorage.getItem('balance_agee_depts_filter')) || []; } catch { return []; }
  });
  const handleDeptsChange = (codes) => {
    setDepts(codes);
    try { localStorage.setItem('balance_agee_depts_filter', JSON.stringify(codes)); } catch { /* quota */ }
  };

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => -d);
    else { setSortCol(col); setSortDir(1); }
  };

  const sortedTiers = data?.tiers ? [...data.tiers].sort((a, b) => {
    const va = sortCol === 'partner_nom' ? (a.partner_nom || '').toLowerCase() : (a[sortCol] ?? 0);
    const vb = sortCol === 'partner_nom' ? (b.partner_nom || '').toLowerCase() : (b[sortCol] ?? 0);
    if (va < vb) return -sortDir;
    if (va > vb) return sortDir;
    return 0;
  }) : [];

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpanded({});
    try {
      const params = { type: tab };
      if (depts.length) params.depts = depts.join(',');
      if (tab === 'client') {
        if (includeTags.length) params.tags_include = includeTags.join(',');
        if (excludeTags.length) params.tags_exclude = excludeTags.join(',');
      }
      const r = await api.getBalanceAgee(params);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [tab, activeAgences, includeTags, excludeTags, depts]); // se re-déclenche si le filtre global change

  useEffect(() => { load(); }, [load]);

  const toggle = (pid) => setExpanded(p => ({ ...p, [pid]: !p[pid] }));
  const expandAll = () => {
    if (!data?.tiers) return;
    const allIds = Object.fromEntries(data.tiers.map(t => [t.partner_id, true]));
    setExpanded(allIds);
  };
  const collapseAll = () => setExpanded({});

  const anyExpanded = Object.values(expanded).some(Boolean);

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)', padding: '24px 32px' }}>

      {/* ── En-tête ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>
            Balances âgées
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            Créances et dettes non lettrées, classées par ancienneté
          </p>
        </div>
        {/* Bouton actualiser */}
        <button
          onClick={load}
          disabled={loading}
          data-testid="btn-actualiser"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors"
          style={{ background: 'var(--card)', borderColor: 'var(--border)', color: 'var(--text)' }}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </button>
      </div>

      {/* ── Filtres ──────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        {/* Onglets clients / fournisseurs */}
        <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border)' }}>
          {[
            { value: 'client',      label: 'Clients'      },
            { value: 'fournisseur', label: 'Fournisseurs' },
          ].map(({ value, label }) => (
            <button
              key={value}
              data-testid={`tab-${value}`}
              onClick={() => setTab(value)}
              className="px-5 py-2 text-sm font-medium transition-colors"
              style={{
                background: tab === value ? '#1D4ED8' : 'var(--card)',
                color:      tab === value ? '#FFFFFF'  : 'var(--text)',
              }}>
              {label}
            </button>
          ))}
        </div>

        {/* Filtre agence */}
        {activeAgences?.length > 0 && (
          <span className="text-xs px-2 py-1 rounded-full font-medium"
            style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
            {activeAgences.length === 1
              ? activeAgences[0].nom
              : `${activeAgences.length} agences`}
          </span>
        )}

        {/* Filtre tags client Inclure/Exclure (tiers_tags) — onglet Clients uniquement */}
        {tab === 'client' && (
          <TiersTagsFilter
            includeIds={includeTags}
            excludeIds={excludeTags}
            onChange={handleTagsChange}
            testidPrefix="balance-tags"
          />
        )}

        {/* Filtre département (préfixe code postal) — onglets Clients ET Fournisseurs */}
        <DepartementFilter
          value={depts}
          onChange={handleDeptsChange}
          testidPrefix="balance-dept"
        />

        {/* Tout déplier / replier */}
        {data?.tiers?.length > 0 && (
          <button
            onClick={anyExpanded ? collapseAll : expandAll}
            className="text-xs px-3 py-1.5 rounded border transition-colors"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)', background: 'var(--card)' }}>
            {anyExpanded ? 'Tout replier' : 'Tout déplier'}
          </button>
        )}

        {/* Nombre de tiers */}
        {data?.tiers && (
          <span className="text-xs px-2 py-1 rounded-full font-medium"
            style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
            {data.tiers.length} tiers
          </span>
        )}
      </div>

      {/* ── Erreur ───────────────────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-xl mb-4 border"
          style={{ background: '#FEF2F2', borderColor: '#FECACA', color: '#DC2626' }}
          data-testid="error-msg">
          <AlertCircle size={16} />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* ── Loading ──────────────────────────────────────────────────────────── */}
      {loading && (
        <div className="flex items-center justify-center py-24" data-testid="loading-state">
          <div className="flex flex-col items-center gap-3">
            <RefreshCw size={28} className="animate-spin text-blue-600" />
            <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Chargement depuis Pennylane…
            </span>
          </div>
        </div>
      )}

      {/* ── Tableau ──────────────────────────────────────────────────────────── */}
      {!loading && data && (
        <div className="rounded-xl border overflow-hidden shadow-sm"
          style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" style={{ minWidth: 960 }}>

              {/* En-têtes colonnes */}
              <thead>
                <tr style={{ background: '#1E3A8A', color: '#FFFFFF' }}>
                  {[
                    { col: 'partner_nom', label: 'Tiers',    align: 'left',   style: { minWidth: 240 } },
                  ].map(({ col, label, align, style: s }) => (
                    <th key={col} onClick={() => toggleSort(col)}
                      className="py-3 px-4 text-xs font-semibold uppercase tracking-wider cursor-pointer select-none"
                      style={{ textAlign: align, ...s }}>
                      {label} {sortCol === col ? (sortDir === 1 ? '↑' : '↓') : <span style={{ opacity: 0.35 }}>↕</span>}
                    </th>
                  ))}
                  <th className="py-3 px-4 text-center text-xs font-semibold uppercase tracking-wider"
                    style={{ minWidth: 110 }}>
                    Échéance
                  </th>
                  {BUCKETS.map(({ key, label }) => (
                    <th key={key} onClick={() => toggleSort(key)}
                      className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wider cursor-pointer select-none"
                      style={{
                        minWidth: 120,
                        borderLeft: key === 'total' ? '2px solid #3B82F6' : undefined,
                        background: key === 'total' ? '#172554' : undefined,
                      }}>
                      {label} {sortCol === key ? (sortDir === 1 ? '↑' : '↓') : <span style={{ opacity: 0.35 }}>↕</span>}
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Corps */}
              <tbody>
                {sortedTiers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-16 text-center text-sm"
                      style={{ color: 'var(--text-muted)' }} data-testid="empty-state">
                      Aucune créance ou dette non lettrée trouvée.
                    </td>
                  </tr>
                ) : (
                  sortedTiers.map(tiers => (
                    <TiersRow
                      key={tiers.partner_id}
                      tiers={tiers}
                      expanded={!!expanded[tiers.partner_id]}
                      onToggle={() => toggle(tiers.partner_id)}
                    />
                  ))
                )}
              </tbody>

              {/* Totaux */}
              {sortedTiers.length > 0 && (
                <tfoot>
                  <TotauxRow totaux={data.totaux} />
                </tfoot>
              )}

            </table>
          </div>
        </div>
      )}

    </div>
  );
}
